import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

export type TabsVariant = 'underline' | 'segmented' | 'pills';
export type TabsSize = 'sm' | 'md';

export interface TabItem<T extends string = string> {
  value: T;
  label: ReactNode;
  count?: number;
  disabled?: boolean;
}

export type TabsItems<T extends string = string> = ReadonlyArray<T | TabItem<T>>;

export interface TabsProps<T extends string = string> {
  value: T;
  onChange?: (value: T) => void;
  items: TabsItems<T>;
  variant?: TabsVariant;
  size?: TabsSize;
  className?: string;
}

const CONTAINER: Record<TabsVariant, string> = {
  underline: 'border-b border-phx-border inline-flex items-stretch gap-0',
  segmented:
    'inline-flex items-stretch gap-0 bg-phx-surface-2 border border-phx-border rounded-phx p-phx-1',
  pills: 'inline-flex items-stretch gap-phx-2',
};

const TAB_BASE =
  'phx-tab relative inline-flex items-center gap-phx-3 px-phx-5 whitespace-nowrap ' +
  'border-0 bg-transparent text-phx-text-2 transition-[color,background-color] ' +
  'hover:text-phx-text disabled:opacity-50 disabled:cursor-not-allowed';

const SIZE_TAB: Record<TabsVariant, Record<TabsSize, string>> = {
  underline: {
    md: 'h-[30px] text-phx-md',
    sm: 'h-phx-8 text-phx-sm px-phx-4',
  },
  segmented: {
    md: 'h-phx-8 text-phx-sm rounded-phx-sm px-phx-4',
    sm: 'h-phx-control-sm text-phx-xs rounded-phx-sm px-phx-3',
  },
  pills: {
    md: 'h-phx-control text-phx-md rounded-phx px-phx-4',
    sm: 'h-phx-8 text-phx-sm rounded-phx px-phx-4',
  },
};

const ACTIVE_CLASSES: Record<TabsVariant, string> = {
  underline:
    'text-phx-primary font-semibold ' +
    "after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-[1px] after:h-phx-1 after:bg-phx-primary",
  segmented: 'bg-phx-surface text-phx-text font-medium shadow-phx-xs',
  pills: 'bg-phx-primary-tint text-phx-primary-active font-medium',
};

const HOVER_PILL = 'hover:bg-phx-surface-2';

export function Tabs<T extends string = string>({
  value,
  onChange,
  items,
  variant = 'underline',
  size = 'md',
  className,
}: TabsProps<T>) {
  return (
    <div
      role="tablist"
      data-variant={variant}
      data-size={size}
      className={cn(CONTAINER[variant], className)}
    >
      {items.map((entry) => {
        const item: TabItem<T> =
          typeof entry === 'string' ? ({ value: entry, label: entry } as TabItem<T>) : entry;
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={item.disabled}
            data-active={active ? '' : undefined}
            className={cn(
              TAB_BASE,
              SIZE_TAB[variant][size],
              variant === 'pills' && !active && HOVER_PILL,
              active && ACTIVE_CLASSES[variant]
            )}
            onClick={() => onChange?.(item.value)}
          >
            <span>{item.label}</span>
            {item.count !== undefined ? (
              <span
                className={cn(
                  'phx-tab-count text-phx-xs h-phx-6 rounded-phx-lg px-phx-2_5 inline-flex items-center',
                  active
                    ? 'bg-phx-primary-tint text-phx-primary-active'
                    : 'bg-phx-surface-2 text-phx-text-2'
                )}
              >
                {item.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
